/**
 * reservationKind.ts — 予約種別（2026-09-19）
 *
 * 予約に「通常 / その他 / 米軍」の種別を持たせる。DB のカラムは増やさず、
 * 予約の json_data.data に日本語キーで持つ（'到着連絡: 有り' / ホテル配車と同じ流儀）。
 *
 *   json_data.data['予約種別'] = '米軍'
 *
 * - キーが無い・空・未知の値 → 「通常」
 * - 「通常」を選んだときはキーを削除する（無指定＝通常なので、書いても情報が増えない）
 * - 既存データは予約名に「米軍」を含むものを移行スクリプトで「米軍」にした
 *   （「軍司」さんのような姓は対象外。詳細は md/master_md/feature-reservation-kind.md）
 */
import { extractJsonDataRecord } from './hotelDelivery';

export const RESERVATION_KIND_KEY = '予約種別';

export const RESERVATION_KINDS = ['通常', 'その他', '米軍'] as const;
export type ReservationKind = typeof RESERVATION_KINDS[number];

export const DEFAULT_RESERVATION_KIND: ReservationKind = '通常';

export const isReservationKind = (v: unknown): v is ReservationKind =>
  typeof v === 'string' && (RESERVATION_KINDS as readonly string[]).includes(v);

/**
 * 米軍予約の表示色（2026-09-24 前田さん指定）。daylist の予約名の背景/文字に使い、
 * 他の予約とひと目で区別する。背景 #584c0f / 文字 アップル・グリーン #97be1c
 * （車両コードは車種色のまま）
 */
export const US_MILITARY_COLORS = { bg: '#584c0f', text: '#97be1c' } as const;

/** 予約オブジェクト（json_data または detail.json_data）が米軍か */
export function isUsMilitaryReservation(reservation: { json_data?: unknown; detail?: { json_data?: unknown } | null } | null | undefined): boolean {
  if (!reservation) return false;
  return getReservationKind(reservation.json_data ?? reservation.detail?.json_data) === '米軍';
}

/** json_data から予約種別を読む。無い・不正なら「通常」 */
export function getReservationKind(jsonData: unknown): ReservationKind {
  const data = extractJsonDataRecord(jsonData);
  const raw = data?.[RESERVATION_KIND_KEY];
  const v = typeof raw === 'string' ? raw.trim() : '';
  return isReservationKind(v) ? v : DEFAULT_RESERVATION_KIND;
}

/**
 * json_data に予約種別を書いた新しい json_data を返す（元は変更しない）。
 * 形式は {data,types} に正規化し、他のキーは維持する。
 */
export function applyReservationKind(
  jsonData: unknown,
  kind: ReservationKind,
): { data: Record<string, unknown>; types: Record<string, unknown> } {
  let jd: unknown = jsonData;
  if (typeof jd === 'string') {
    try { jd = JSON.parse(jd); } catch { jd = null; }
  }
  if (Array.isArray(jd)) jd = jd[0];
  const base = (jd && typeof jd === 'object') ? (jd as Record<string, unknown>) : {};
  const data: Record<string, unknown> = { ...((base.data as Record<string, unknown>) || {}) };
  const types: Record<string, unknown> = { ...((base.types as Record<string, unknown>) || {}) };

  if (kind === DEFAULT_RESERVATION_KIND) {
    delete data[RESERVATION_KIND_KEY];
    delete types[RESERVATION_KIND_KEY];
  } else {
    data[RESERVATION_KIND_KEY] = kind;
    types[RESERVATION_KIND_KEY] = 'text';
  }
  return { ...base, data, types };
}
